import useSessionEvents from '@/composables/v2/metrics/useSessionEvents';
import { ref } from 'vue';
import { SessionEvent } from '@/models/Metrics/SessionEvent';

const isDebug = process.env.VUE_APP_DEBUG === 'true';

/**
 * Represents the identifier of the session for which events are being tracked.
 */
const sessionId = ref<string | undefined>(undefined);

const METRICS_FLUSHING_INTERVAL = Number(process.env.VUE_APP_METRICS_FLUSHING_INTERVAL ?? 5000); // In milliseconds

/**
 * Identifier of the periodic flush interval. It is owned by `setObservedSessionId`, so it lives
 * as long as the observed session and not as long as a component.
 */
let flushingIntervalId: ReturnType<typeof setInterval> | null = null;

export function setObservedSessionId(id: string | undefined): void {
    if (id === sessionId.value) {
        return;
    }

    if (flushingIntervalId !== null) {
        clearInterval(flushingIntervalId);
        flushingIntervalId = null;
    }

    sessionId.value = id;

    if (!id) {
        return;
    }

    const { restoreEventsFromLocalStorage, flushSessionEvents } = useSessionEvents();
    restoreEventsFromLocalStorage(id);

    flushingIntervalId = setInterval(() => {
        const observedSessionId = sessionId.value;
        if (observedSessionId) {
            flushSessionEvents(observedSessionId);
        }
    }, METRICS_FLUSHING_INTERVAL);
}

export function getObservedSessionId(): string | undefined {
    return sessionId.value;
}

const METRICS_MAX_BUFFER_SIZE = process.env.VUE_APP_METRICS_MAX_BUFFER_SIZE ?? 10; // Session events are flushed when the buffer size reaches this value

/**
 * Manages session metrics including event restoration, buffering, and periodic flushing of session events.
 *
 * @return {Object} An object containing methods to dispatch session events.
 * @return {Function} return.dispatchSessionEvents - A method to dispatch session events. Allows adding events to the buffer and triggers flushing based on conditions.
 * @param newObservedSessionId
 */
export default function useMetrics(newObservedSessionId: string | undefined = undefined) {
    const { storeSessionEventInBuffer, flushSessionEvents, eventsBuffer } = useSessionEvents();

    if (newObservedSessionId) {
        setObservedSessionId(newObservedSessionId);
    }

    const flushSessionEventsIfCurrentSessionExist = async () => {
        const currentSessionId = sessionId.value;
        if (!currentSessionId) {
            return;
        }
        await flushSessionEvents(currentSessionId);
    };

    const dispatchSessionEvents = (events: SessionEvent[], now = false): void => {
        if (isDebug) {
            console.log('Dispatching event:');
            console.table(events[0]);
        }

        const currentSessionId = sessionId.value;

        if (!currentSessionId) {
            console.warn('A Session ID must be provided to send session events.');
            return;
        }

        events.forEach((event) => {
            storeSessionEventInBuffer(currentSessionId, event);
        });

        const eventsBufferSize = (eventsBuffer[currentSessionId] ?? []).length;

        if (now || eventsBufferSize >= METRICS_MAX_BUFFER_SIZE) {
            flushSessionEvents(currentSessionId);
        }
    };

    const forceFlushSessionEvents = (): void => {
        dispatchSessionEvents([], true);
    };

    return {
        dispatchSessionEvents,
        forceFlushSessionEvents,
        flushSessionEventsIfCurrentSessionExist,
    };
}
